import React, { useState } from 'react'
import CustomDropdown from 'components/ui/dropdowns/custom-dropdown'
import { debounce } from 'lodash'
import type { Contact, ContactId } from 'types/list'
import type { DropdownOption } from 'types/ui'
import api from '@disco/api/server'
import { validateEmail } from '@disco/utils/validation'

const loadOptions = debounce(
  (
    input: string,
    callback: (error: any, { options }: { options: DropdownOption[] }) => void,
  ) => {
    ;(async () => {
      if (!input) {
        return callback(null, {
          options: [],
        })
      }

      const getOptionsFromResponse = (results: Contact[]) => {
        return results
          ?.filter(contact => !contact?.unsubscribes?.global)
          .map(contact => ({
            label: Boolean(contact.name) ? contact.name : contact.email,
            value: contact.id,
            data: contact,
          }))
      }

      let options: ExtendedDropdownOption[] = []
      try {
        const { results } = await api.getContacts(
          input,
          undefined,
          undefined,
          20,
        )
        options = getOptionsFromResponse(results)
      } catch {
        options = []
      }

      // If input is a valid email, append it as an option and not match any existing options
      if (
        validateEmail(input) &&
        !options.find(option => option.data.email === input)
      ) {
        options = [
          ...options,
          {
            label: input,
            value: input,
            data: { type: 'email', email: input },
          },
        ]
      }

      return callback(null, {
        options: options,
      })
    })()
  },
  300,
)

export type ExtendedDropdownOption = DropdownOption & {
  data: Contact | { type: 'email'; email: string }
}

export const ContactsDropdown = ({
  placeholder,
  onChange,
  className,
  value,
}: {
  placeholder: string
  onChange: (value: ExtendedDropdownOption) => void
  className?: string
  value?: ExtendedDropdownOption
}) => {
  const handleChange = (selectedOption: ExtendedDropdownOption) => {
    onChange(selectedOption)
  }

  return (
    <CustomDropdown
      value={value}
      name="contacts-dropdown"
      placeholder={placeholder}
      className={className}
      onChange={handleChange}
      async
      loadOptions={loadOptions}
    />
  )
}
